import React, { useState, useEffect, useRef } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { AuthRoute, ProtectedRoute } from './Components/Dashboard/DashboardRouter_util'
import LogInContainer from './Components/LoginPage/login_page_container'
import DashboardRouter from './Components/Dashboard/DashboardRouter'
import NotFoundPage from "./Components/Templates/NotFoundPage"
import { HashRouter } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { checkAndRefreshIfNeeded, refreshUser, logout } from "./redux/actions/session_actions"
import { online } from "./util/user_util"

import WebSocketRecieverContainer from "./Components/Websockets/web_socket_receiver_container";
import 'react-sortable-tree/style.css';

// --- Constants ---
const ACCESS_TOKEN_EXPIRY_WARNING_SECONDS = 60; // Show warning modal this many seconds before expiry
const REFRESH_BUFFER_SECONDS = 30; // Try to refresh this many seconds before expiry (changed from 60 for testing)
const REFRESH_TOKEN_EXPIRY_WARNING_SECONDS = 60; // Show warning modal this many seconds before refresh token expiry
// ---------------

function App() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  // --- JWT Auto-Refresh Logic State ---
  const dispatch = useDispatch();
  const session = useSelector(state => state.session);
  // State for Access Token Timer (used for triggering refresh)
  const [accessTokenRemainingTimeInternal, setAccessTokenRemainingTimeInternal] = useState(null);
  const intervalRef = useRef(null);
  const refreshTriggeredRef = useRef(false);
  // ---------------------------------

  // --- Modal State & Refresh Token Timer State ---
  const [showExpiryWarningModal, setShowExpiryWarningModal] = useState(false);
  const [refreshTokenRemainingTime, setRefreshTokenRemainingTime] = useState(null); // For modal display
  const [warningDismissedThisCycle, setWarningDismissedThisCycle] = useState(false); // <-- Add state for dismissal
  const refreshWarningIntervalRef = useRef(null);
  const previousRefreshTokenExpRef = useRef(null); // <-- Add ref to track expiry changes
  // ---------------------------------------------

  // --- Backend Reachability State ---
  const [isBackendReachable, setIsBackendReachable] = useState(true); // Assume online initially
  const onlineCheckIntervalRef = useRef(null);
  // ---------------------------------

  useEffect(() => {
    const handleSWUpdate = (event) => {
      console.log("SW Update event received in App.js", event.detail);
      setWaitingWorker(event.detail.waiting);
      setUpdateAvailable(true);
    };

    window.addEventListener('swUpdate', handleSWUpdate);

    return () => {
      window.removeEventListener('swUpdate', handleSWUpdate);
    };
  }, []);

  // --- JWT Auto-Refresh Logic Effect ---
  useEffect(() => {
    // Clear any existing interval when session changes
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setAccessTokenRemainingTimeInternal(null);
    setShowExpiryWarningModal(false); // Ensure modal is hidden on session change

    if (session.isAuthenticated && session.user && session.user.exp) {
      const expirationTime = session.user.exp * 1000;
      const now = Date.now();
      const timeLeft = Math.max(0, Math.floor((expirationTime - now) / 1000));

      setAccessTokenRemainingTimeInternal(timeLeft);

      if (timeLeft > 0) {
        intervalRef.current = setInterval(() => {
          setAccessTokenRemainingTimeInternal(prevTime => {
            if (prevTime === null) return 0; // Prevent NaN if state updates slowly
            const newTime = prevTime - 1;

            // Check if it's time to trigger ACCESS TOKEN refresh check (only once per cycle)
            // --- DISABLED AUTOMATIC REFRESH FOR OFFLINE TEST ---
            /* 
            if (newTime > 0 && newTime <= REFRESH_BUFFER_SECONDS && !refreshTriggeredRef.current) {
              refreshTriggeredRef.current = true; // Mark as triggered *for this token cycle*
              console.log(`[Interval Callback] Access token nearing expiry (${newTime}s left). Dispatching checkAndRefreshIfNeeded thunk.`);
              // Dispatch the thunk action, which will access current state
              dispatch(checkAndRefreshIfNeeded()); 
            }
            */
            // --- END DISABLED SECTION ---

            if (newTime > 0) {
              return newTime;
            } else {
              console.log('[Interval Callback] Access Token Timer Reached Zero!');
              clearInterval(intervalRef.current);
              intervalRef.current = null;
              setShowExpiryWarningModal(false); // Hide modal when timer hits zero
              // Check auth status again *after* giving the potential refresh action time to complete.
              // Using a timeout is a pragmatic approach here without more complex state listeners.
              setTimeout(() => {
                 // Re-select the latest state within the timeout
                 // Note: This requires the store instance or re-using useSelector if inside a component scope
                 // For simplicity, let's assume a mechanism to get current state, or logout if not possible.
                 // We can directly dispatch logout, as the refresh *should* have kept isAuthenticated true if successful.
                 // If it's false now, logout is appropriate.
                 console.log("[Interval Timeout Check] Checking authentication status...");
                 // Re-dispatching logout might be redundant if refreshUser already did on failure, 
                 // but acts as a safeguard if the token just silently expired.
                 // Let's check the flag first - if refresh *was* triggered, let refreshUser handle logout.
                 // If refresh was *not* triggered (e.g., long time inactive), then logout here.
                 if (!refreshTriggeredRef.current) { 
                    console.log("[Interval Timeout Check] Refresh was not triggered before expiry. Logging out.");
                    dispatch(logout());
                 } else {
                    console.log("[Interval Timeout Check] Refresh was triggered. Assuming refreshUser handled logout if needed.");
                 }
                 // Alternatively, directly check state if store is available:
                 // const latestState = store.getState();
                 // if (!latestState.session.isAuthenticated) { ... dispatch(logout()); ... }
              }, 500); // Check status shortly after timer hits zero
              return 0;
            }
          });
        }, 1000);
      } else {
        // Access token already expired on load

        // Dispatch the thunk immediately to attempt refresh if possible (thunk handles online check)
        dispatch(checkAndRefreshIfNeeded());
      }
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setShowExpiryWarningModal(false); // Ensure modal is hidden on cleanup
        console.log("Cleaned up access token auto-refresh interval.");
      }
    };
    // Keep dependencies
  }, [session.isAuthenticated, session.user, dispatch]);

  // --- Refresh Token Expiry Warning Effect ---
  useEffect(() => {
    // Clear any existing interval for the warning timer
    if (refreshWarningIntervalRef.current) {
      clearInterval(refreshWarningIntervalRef.current);
      refreshWarningIntervalRef.current = null;
    }
    // Reset state variables for the effect
    setRefreshTokenRemainingTime(null); 
    setShowExpiryWarningModal(false); 

    // Reset dismissal state if the token expiry has actually changed
    if (session.refreshTokenExp !== previousRefreshTokenExpRef.current) {
      console.log("[App.js Effect] Refresh token expiry changed. Resetting dismissal flag.");
      setWarningDismissedThisCycle(false);
      previousRefreshTokenExpRef.current = session.refreshTokenExp; // Update the tracked expiry
    }

    if (session.isAuthenticated && session.refreshTokenExp) {
      const calculateRefreshRemaining = () => {
        const now = Date.now();
        const expiryTime = session.refreshTokenExp * 1000; // Convert expiry to ms
        const timeLeft = Math.max(0, Math.floor((expiryTime - now) / 1000)); // Remaining seconds

        setRefreshTokenRemainingTime(timeLeft);

        // Show/hide modal based on remaining time AND dismissal state
        if (timeLeft > 0 && timeLeft <= REFRESH_TOKEN_EXPIRY_WARNING_SECONDS && !warningDismissedThisCycle) {
          setShowExpiryWarningModal(true);
        } else {
          setShowExpiryWarningModal(false);
        }

        // Stop interval if time runs out
        if (timeLeft <= 0) {
          console.log("[App.js] Refresh token expired. Dispatching logout.");
          dispatch(logout());
        }
      };

      calculateRefreshRemaining(); // Run once immediately

      // Only set interval if time is remaining after initial calculation
      const initialTimeLeft = Math.max(0, Math.floor((session.refreshTokenExp * 1000 - Date.now()) / 1000));
      if (initialTimeLeft > 0) {
        refreshWarningIntervalRef.current = setInterval(calculateRefreshRemaining, 1000); // Update every second
      }
    }

    // Cleanup function
    return () => {
      if (refreshWarningIntervalRef.current) {
        clearInterval(refreshWarningIntervalRef.current);
        refreshWarningIntervalRef.current = null;
        setShowExpiryWarningModal(false); // Hide modal on cleanup
        console.log("Cleaned up refresh token warning interval.");
      }
    };
    // Rerun only if auth status or refresh token expiry changes
  }, [session.isAuthenticated, session.refreshTokenExp, dispatch, warningDismissedThisCycle]); // <-- Add warningDismissedThisCycle to dependencies
  // -------------------------------------------

  // --- Backend Reachability Polling Effect ---
  useEffect(() => {
      let isMounted = true;

      const performOnlineCheck = () => {
          online().then(isOnline => {
              if (isMounted) {
                  setIsBackendReachable(isOnline);
              }
          }).catch(err => {
              console.error("[App.js] Error during periodic online check:", err);
              if (isMounted) {
                  setIsBackendReachable(false); // Assume offline on error
              }
          });
      };

      // Initial check
      performOnlineCheck();

      // Setup polling (e.g., every 15 seconds)
      onlineCheckIntervalRef.current = setInterval(performOnlineCheck, 15000);

      // Cleanup
      return () => {
          isMounted = false;
          if (onlineCheckIntervalRef.current) {
              clearInterval(onlineCheckIntervalRef.current);
          }
      };
  }, []); // Empty dependency array ensures this runs only once on mount
  // -------------------------------------------

  // --- JWT Prompt Handlers ---
  // Function to handle manual refresh triggered by the user
  const handleManualRefresh = () => {
    console.log("[App.js] Manual refresh requested by user.");
    if (localStorage.refreshToken) {
        dispatch(refreshUser(localStorage.refreshToken));
        setShowExpiryWarningModal(false); // Hide modal after initiating refresh
    } else {
        console.warn("[App.js] Cannot manually refresh: Refresh token not found in localStorage.");
        // Optionally dispatch logout here if refresh token is missing unexpectedly
        // dispatch(logout()); 
    }
  };
  // const handleTokenLogout = () => { ... };
  // ---------------------------

  const handleReload = () => {
    console.log("handleReload called");
    if (waitingWorker) {
      console.log("waitingWorker found, adding controllerchange listener then posting SKIP_WAITING");
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log("Controller changed!");
        if (refreshing) return;
        refreshing = true;
        console.log("Reloading page due to controller change.");
        // eslint-disable-next-line no-restricted-globals
        window.location.reload();
      }, { once: true });
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    } else {
       console.log("waitingWorker NOT found in handleReload");
    }
  };

  return (
    //https://stackoverflow/com/questions/51974369/what-is-the-difference-between-hashrouter-and-browserrouter-in-react
    <HashRouter>
      {/* Display Update Button if available */}
      {updateAvailable && (
        <div style={{ position: 'fixed', bottom: '20px', left: '20px', backgroundColor: 'orange', padding: '10px', borderRadius: '5px', zIndex: 1000 }}>
          <span>A new version is available.</span>
          <button onClick={handleReload} style={{ marginLeft: '10px' }}>
            Please Refresh
          </button>
        </div>
      )}

      {/* --- Access Token Expiry Warning Modal --- */}
      {showExpiryWarningModal && session.isAuthenticated && (
        <div style={{
          position: 'fixed', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          backgroundColor: '#081f5f', // Orange background 
          color: 'white', 
          padding: '20px', 
          borderRadius: '8px', 
          zIndex: 2000, // Ensure it's above other elements
          border: '1px solid white',
          textAlign: 'center'
        }}>
          <h4 style={{ color: 'white' }}>Session Expiry Warning</h4>
          <p style={{ color: 'white' }}>Your session will fully expire in ~{formatTime(refreshTokenRemainingTime)}.</p>
          
          {/* Conditionally render Refresh button only when online */}
          {isBackendReachable && (
            <button 
              onClick={handleManualRefresh} 
              style={{ 
                  marginTop: '15px', 
                  marginRight: '10px', // Add some space between buttons
                  padding: '8px 15px', 
                  cursor: 'pointer',
                  backgroundColor: '#4CAF50', /* Green */
                  border: 'none',
                  color: 'white',
                  borderRadius: '4px'
              }}
            >
              Refresh Session
            </button>
          )}

          {/* Add the Dismiss button */}
          <button 
            onClick={() => {
              setShowExpiryWarningModal(false); // Hide modal
              setWarningDismissedThisCycle(true); // Set dismissal flag for this cycle
            }} 
            style={{
                marginTop: '15px',
                padding: '8px 15px', 
                cursor: 'pointer',
                backgroundColor: '#f44336', /* Red */
                border: 'none',
                color: 'white',
                borderRadius: '4px'
            }}
          >
            Dismiss
          </button>
        </div>
      )}
      {/* ---------------------------------------- */}

      <WebSocketRecieverContainer />
      
      <Switch>
        <Redirect exact path="/" to="/login" />
        <AuthRoute exact path="/login" component={LogInContainer} />
        <ProtectedRoute path="/dashboard" component={DashboardRouter} />
        <Route render={() => <NotFoundPage /> } />
      </Switch>
    </HashRouter>
  );
}

// Helper function to format time (MM:SS) - Copied here for the modal
const formatTime = (totalSeconds) => {
    if (totalSeconds === null || totalSeconds < 0) return "--:--";
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export default App;
